import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosGateway } from "../../../api/axios";

const GATEWAY_URL = "/medical-cases";
const LIVE_BASE = "/lives";

const normalizeResponse = (res) => res?.data?.data ?? res?.data;
const normalizeList = (payload) =>
  Array.isArray(payload) ? payload : payload?.items || [];

/* ======================== CASES ======================== */

/**
 * Créer un cas médical
 */
export const createMedicalCase = createAsyncThunk(
  "medicalCases/createCase",
  async (caseData, { rejectWithValue }) => {
    try {
      const res = await axiosGateway.post(GATEWAY_URL, caseData);
      return normalizeResponse(res);
    } catch (err) {
      console.error("[medicalCasesThunks] createMedicalCase error:", err);
      return rejectWithValue(
        err?.response?.data?.message || "Failed to create case"
      );
    }
  }
);

/**
 * Récupérer mes cas
 */
export const fetchMyCases = createAsyncThunk(
  "medicalCases/fetchMyCases",
  async ({ page = 1, limit = 10 } = {}, { rejectWithValue }) => {
    try {
      const res = await axiosGateway.get(`${GATEWAY_URL}/mine`, {
        params: { page, limit },
      });
      return normalizeResponse(res);
    } catch (err) {
      console.error("[medicalCasesThunks] fetchMyCases error:", err);
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch my cases"
      );
    }
  }
);

/**
 * Récupérer les cas publics
 */
export const fetchPublicCases = createAsyncThunk(
  "medicalCases/fetchPublicCases",
  async ({ page = 1, limit = 10 } = {}, { rejectWithValue }) => {
    try {
      const res = await axiosGateway.get(`${GATEWAY_URL}/public`, {
        params: { page, limit },
      });
      return normalizeResponse(res);
    } catch (err) {
      console.error("[medicalCasesThunks] fetchPublicCases error:", err);
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch public cases"
      );
    }
  }
);

/**
 * Récupérer un cas par ID
 */
export const fetchCaseById = createAsyncThunk(
  "medicalCases/fetchCaseById",
  async (caseId, { rejectWithValue }) => {
    try {
      if (!caseId) throw new Error("caseId is required");
      const res = await axiosGateway.get(`${GATEWAY_URL}/${caseId}`);
      return normalizeResponse(res);
    } catch (err) {
      console.error("[medicalCasesThunks] fetchCaseById error:", err);
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch case"
      );
    }
  }
);

/**
 * Modifier un cas
 */
export const updateMedicalCase = createAsyncThunk(
  "medicalCases/updateCase",
  async ({ caseId, updates }, { rejectWithValue }) => {
    try {
      if (!caseId) throw new Error("caseId is required");
      const res = await axiosGateway.patch(`${GATEWAY_URL}/${caseId}`, updates);
      return normalizeResponse(res);
    } catch (err) {
      console.error("[medicalCasesThunks] updateMedicalCase error:", err);
      return rejectWithValue(
        err?.response?.data?.message || "Failed to update case"
      );
    }
  }
);

/**
 * Supprimer un cas
 */
export const deleteMedicalCase = createAsyncThunk(
  "medicalCases/deleteCase",
  async (caseId, { rejectWithValue }) => {
    try {
      if (!caseId) throw new Error("caseId is required");
      await axiosGateway.delete(`${GATEWAY_URL}/${caseId}`);
      return caseId;
    } catch (err) {
      console.error("[medicalCasesThunks] deleteMedicalCase error:", err);
      return rejectWithValue(
        err?.response?.data?.message || "Failed to delete case"
      );
    }
  }
);

/* ======================== ATTACHMENTS ======================== */

/**
 * Ajouter des fichiers à un cas
 */
export const addAttachments = createAsyncThunk(
  "medicalCases/addAttachments",
  async ({ caseId, files }, { rejectWithValue }) => {
    try {
      if (!caseId) throw new Error("caseId is required");
      if (!files || files.length === 0) throw new Error("Files are required");

      const formData = new FormData();
      files.forEach((file) => formData.append("files", file));

      const res = await axiosGateway.post(
        `${GATEWAY_URL}/${caseId}/attachments`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      // Return caseId alongside the payload so the reducer can sync currentCase.data
      return { caseId, attachments: normalizeResponse(res) };
    } catch (err) {
      console.error("[medicalCasesThunks] addAttachments error:", err);
      return rejectWithValue(
        err?.response?.data?.message || "Failed to add attachments"
      );
    }
  }
);

/**
 * Obtenir une pièce jointe
 */
export const fetchAttachment = createAsyncThunk(
  "medicalCases/fetchAttachment",
  async ({ caseId, attachmentId }, { rejectWithValue }) => {
    try {
      if (!caseId || !attachmentId)
        throw new Error("caseId and attachmentId are required");
      const res = await axiosGateway.get(
        `${GATEWAY_URL}/${caseId}/attachments/${attachmentId}`
      );
      return normalizeResponse(res);
    } catch (err) {
      console.error("[medicalCasesThunks] fetchAttachment error:", err);
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch attachment"
      );
    }
  }
);

/**
 * Supprimer une pièce jointe
 */
export const deleteAttachment = createAsyncThunk(
  "medicalCases/deleteAttachment",
  async ({ caseId, attachmentId }, { rejectWithValue }) => {
    try {
      if (!caseId || !attachmentId)
        throw new Error("caseId and attachmentId are required");
      await axiosGateway.delete(
        `${GATEWAY_URL}/${caseId}/attachments/${attachmentId}`
      );
      return { caseId, attachmentId };
    } catch (err) {
      console.error("[medicalCasesThunks] deleteAttachment error:", err);
      return rejectWithValue(
        err?.response?.data?.message || "Failed to delete attachment"
      );
    }
  }
);

/* ======================== ANNOTATIONS ======================== */

/**
 * Créer une annotation
 */
export const createAnnotation = createAsyncThunk(
  "medicalCases/createAnnotation",
  async ({ caseId, attachmentId, annotation }, { rejectWithValue }) => {
    try {
      if (!caseId || !attachmentId)
        throw new Error("caseId and attachmentId are required");
      const res = await axiosGateway.post(
        `${GATEWAY_URL}/${caseId}/attachments/${attachmentId}/annotations`,
        annotation
      );
      return normalizeResponse(res);
    } catch (err) {
      console.error("[medicalCasesThunks] createAnnotation error:", err);
      return rejectWithValue(
        err?.response?.data?.message || "Failed to create annotation"
      );
    }
  }
);

/**
 * Récupérer les annotations
 */
export const fetchAnnotations = createAsyncThunk(
  "medicalCases/fetchAnnotations",
  async ({ caseId, attachmentId }, { rejectWithValue }) => {
    try {
      if (!caseId || !attachmentId)
        throw new Error("caseId and attachmentId are required");
      const res = await axiosGateway.get(
        `${GATEWAY_URL}/${caseId}/attachments/${attachmentId}/annotations`
      );
      return normalizeResponse(res);
    } catch (err) {
      console.error("[medicalCasesThunks] fetchAnnotations error:", err);
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch annotations"
      );
    }
  }
);

/**
 * Modifier une annotation
 */
export const updateAnnotation = createAsyncThunk(
  "medicalCases/updateAnnotation",
  async ({ caseId, attachmentId, annotationId, updates }, { rejectWithValue }) => {
    try {
      if (!caseId || !attachmentId || !annotationId)
        throw new Error("caseId, attachmentId and annotationId are required");
      const res = await axiosGateway.patch(
        `${GATEWAY_URL}/${caseId}/attachments/${attachmentId}/annotations/${annotationId}`,
        updates
      );
      return normalizeResponse(res);
    } catch (err) {
      console.error("[medicalCasesThunks] updateAnnotation error:", err);
      return rejectWithValue(
        err?.response?.data?.message || "Failed to update annotation"
      );
    }
  }
);

/**
 * Supprimer une annotation
 */
export const deleteAnnotation = createAsyncThunk(
  "medicalCases/deleteAnnotation",
  async ({ caseId, attachmentId, annotationId }, { rejectWithValue }) => {
    try {
      if (!caseId || !attachmentId || !annotationId)
        throw new Error("caseId, attachmentId and annotationId are required");
      await axiosGateway.delete(
        `${GATEWAY_URL}/${caseId}/attachments/${attachmentId}/annotations/${annotationId}`
      );
      return { caseId, attachmentId, annotationId };
    } catch (err) {
      console.error("[medicalCasesThunks] deleteAnnotation error:", err);
      return rejectWithValue(
        err?.response?.data?.message || "Failed to delete annotation"
      );
    }
  }
);

/* ======================== LIKES ======================== */

/**
 * Activer/retirer un like
 */
export const toggleCaseLike = createAsyncThunk(
  "medicalCases/toggleLike",
  async (caseId, { rejectWithValue }) => {
    try {
      if (!caseId) throw new Error("caseId is required");
      const res = await axiosGateway.post(`${GATEWAY_URL}/${caseId}/likes`);
      return normalizeResponse(res);
    } catch (err) {
      console.error("[medicalCasesThunks] toggleCaseLike error:", err);
      return rejectWithValue(
        err?.response?.data?.message || "Failed to toggle like"
      );
    }
  }
);

/**
 * Récupérer les likes
 */
export const fetchCaseLikes = createAsyncThunk(
  "medicalCases/fetchLikes",
  async (caseId, { rejectWithValue }) => {
    try {
      if (!caseId) throw new Error("caseId is required");
      const res = await axiosGateway.get(`${GATEWAY_URL}/${caseId}/likes`);
      return normalizeResponse(res);
    } catch (err) {
      console.error("[medicalCasesThunks] fetchCaseLikes error:", err);
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch likes"
      );
    }
  }
);

/* ======================== COMMENTS ======================== */

/**
 * Créer un commentaire
 */
export const createCaseComment = createAsyncThunk(
  "medicalCases/createComment",
  async ({ caseId, content }, { rejectWithValue }) => {
    try {
      if (!caseId) throw new Error("caseId is required");
      const res = await axiosGateway.post(`${GATEWAY_URL}/${caseId}/comments`, {
        content,
      });
      return normalizeResponse(res);
    } catch (err) {
      console.error("[medicalCasesThunks] createCaseComment error:", err);
      return rejectWithValue(
        err?.response?.data?.message || "Failed to create comment"
      );
    }
  }
);

/**
 * Récupérer les commentaires
 */
export const fetchCaseComments = createAsyncThunk(
  "medicalCases/fetchComments",
  async (caseId, { rejectWithValue }) => {
    try {
      if (!caseId) throw new Error("caseId is required");
      const res = await axiosGateway.get(`${GATEWAY_URL}/${caseId}/comments`);
      return normalizeResponse(res);
    } catch (err) {
      console.error("[medicalCasesThunks] fetchCaseComments error:", err);
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch comments"
      );
    }
  }
);

/**
 * Modifier un commentaire
 */
export const updateCaseComment = createAsyncThunk(
  "medicalCases/updateComment",
  async ({ caseId, commentId, content }, { rejectWithValue }) => {
    try {
      if (!caseId || !commentId)
        throw new Error("caseId and commentId are required");
      const res = await axiosGateway.patch(
        `${GATEWAY_URL}/${caseId}/comments/${commentId}`,
        { content }
      );
      return normalizeResponse(res);
    } catch (err) {
      console.error("[medicalCasesThunks] updateCaseComment error:", err);
      return rejectWithValue(
        err?.response?.data?.message || "Failed to update comment"
      );
    }
  }
);

/**
 * Supprimer un commentaire
 */
export const deleteCaseComment = createAsyncThunk(
  "medicalCases/deleteComment",
  async ({ caseId, commentId }, { rejectWithValue }) => {
    try {
      if (!caseId || !commentId)
        throw new Error("caseId and commentId are required");
      await axiosGateway.delete(`${GATEWAY_URL}/${caseId}/comments/${commentId}`);
      return { caseId, commentId };
    } catch (err) {
      console.error("[medicalCasesThunks] deleteCaseComment error:", err);
      return rejectWithValue(
        err?.response?.data?.message || "Failed to delete comment"
      );
    }
  }
);

/* ======================== LIVES ======================== */

export const createLive = createAsyncThunk(
  "medicalCases/createLive",
  async (liveData, { rejectWithValue }) => {
    try {
      const payload = { ...liveData };

      // backend /lives contract does not accept a raw specialty field here
      if (payload.specialty !== undefined) {
        delete payload.specialty;
      }

      const rawMaxParticipants = payload?.maxParticipants;
      const parsedMaxParticipants = Number(rawMaxParticipants);

      if (rawMaxParticipants !== undefined) {
        payload.maxParticipants = Number.isFinite(parsedMaxParticipants)
          ? Math.max(2, Math.round(parsedMaxParticipants))
          : 2;
      }

      const res = await axiosGateway.post(LIVE_BASE, payload);
      return normalizeResponse(res);
    } catch (err) {
      console.error("[medicalCasesThunks] createLive error:", err);
      return rejectWithValue(
        err?.response?.data?.message || "Failed to create live"
      );
    }
  }
);

export const fetchMyLives = createAsyncThunk(
  "medicalCases/fetchMyLives",
  async ({ page = 1, limit = 20 } = {}, { rejectWithValue }) => {
    try {
      const res = await axiosGateway.get(`${LIVE_BASE}/mine`, {
        params: { page, limit },
      });
      return normalizeResponse(res);
    } catch (err) {
      console.error("[medicalCasesThunks] fetchMyLives error:", err);
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch my lives"
      );
    }
  }
);

export const fetchPublicLives = createAsyncThunk(
  "medicalCases/fetchPublicLives",
  async ({ page = 1, limit = 20 } = {}, { rejectWithValue }) => {
    try {
      const res = await axiosGateway.get(`${LIVE_BASE}/public`, {
        params: { page, limit },
      });
      return normalizeResponse(res);
    } catch (err) {
      console.error("[medicalCasesThunks] fetchPublicLives error:", err);
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch public lives"
      );
    }
  }
);

export const fetchLiveById = createAsyncThunk(
  "medicalCases/fetchLiveById",
  async (liveId, { rejectWithValue }) => {
    try {
      if (!liveId) throw new Error("liveId is required");
      const res = await axiosGateway.get(`${LIVE_BASE}/${liveId}`);
      return normalizeResponse(res);
    } catch (err) {
      console.error("[medicalCasesThunks] fetchLiveById error:", err);
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch live"
      );
    }
  }
);

export const updateLive = createAsyncThunk(
  "medicalCases/updateLive",
  async ({ liveId, updates }, { rejectWithValue }) => {
    try {
      if (!liveId) throw new Error("liveId is required");
      const res = await axiosGateway.patch(`${LIVE_BASE}/${liveId}`, updates);
      return normalizeResponse(res);
    } catch (err) {
      console.error("[medicalCasesThunks] updateLive error:", err);
      return rejectWithValue(
        err?.response?.data?.message || "Failed to update live"
      );
    }
  }
);

/*
 * Le backend n'active jamais physiquement la caméra/le micro : il ne fait
 * qu'autoriser la publication et retourner un token LiveKit. Les endpoints
 * de cycle de vie (start/join/leave/end/cancel) sont appelés SANS body,
 * conformément au contrat documenté — on n'envoie donc plus `{}` en second
 * argument (ce qui forçait un Content-Type: application/json avec un body
 * "{}" non vide), mais bien aucun body du tout.
 */

export const startLive = createAsyncThunk(
  "medicalCases/startLive",
  async (liveId, { rejectWithValue }) => {
    try {
      if (!liveId) throw new Error("liveId is required");
      const res = await axiosGateway.post(`${LIVE_BASE}/${liveId}/start`);
      return normalizeResponse(res);
    } catch (err) {
      console.error("[medicalCasesThunks] startLive error:", err);
      return rejectWithValue(
        err?.response?.data?.message || "Failed to start live"
      );
    }
  }
);

export const joinLive = createAsyncThunk(
  "medicalCases/joinLive",
  async (liveId, { rejectWithValue }) => {
    try {
      if (!liveId) throw new Error("liveId is required");
      const res = await axiosGateway.post(`${LIVE_BASE}/${liveId}/join`);
      return normalizeResponse(res);
    } catch (err) {
      console.error("[medicalCasesThunks] joinLive error:", err);
      return rejectWithValue(
        err?.response?.data?.message || "Failed to join live"
      );
    }
  }
);

export const fetchLiveParticipants = createAsyncThunk(
  "medicalCases/fetchLiveParticipants",
  async ({ liveId, page = 1, limit = 100 }, { rejectWithValue }) => {
    try {
      if (!liveId) throw new Error("liveId is required");
      const res = await axiosGateway.get(`${LIVE_BASE}/${liveId}/participants`, {
        params: { page, limit },
      });
      return { liveId, payload: normalizeResponse(res) };
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch live participants"
      );
    }
  }
);

export const requestJoinLive = createAsyncThunk(
  "medicalCases/requestJoinLive",
  async (liveId, { rejectWithValue }) => {
    try {
      if (!liveId) throw new Error("liveId is required");
      const res = await axiosGateway.post(`${LIVE_BASE}/${liveId}/join-requests`);
      return normalizeResponse(res);
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to request to join"
      );
    }
  }
);

export const fetchMyJoinRequests = createAsyncThunk(
  "medicalCases/fetchMyJoinRequests",
  async ({ page = 1, limit = 20 } = {}, { rejectWithValue }) => {
    try {
      const res = await axiosGateway.get(`${LIVE_BASE}/join-requests/mine`, {
        params: { page, limit },
      });
      return normalizeResponse(res);
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch my join requests"
      );
    }
  }
);

export const fetchJoinRequests = createAsyncThunk(
  "medicalCases/fetchJoinRequests",
  async ({ liveId, page = 1, limit = 50 }, { rejectWithValue }) => {
    try {
      if (!liveId) throw new Error("liveId is required");
      const res = await axiosGateway.get(`${LIVE_BASE}/${liveId}/join-requests`, {
        params: { page, limit },
      });
      return { liveId, payload: normalizeResponse(res) };
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch join requests"
      );
    }
  }
);

export const approveJoinRequest = createAsyncThunk(
  "medicalCases/approveJoinRequest",
  async ({ liveId, requestId }, { rejectWithValue }) => {
    try {
      const res = await axiosGateway.patch(
        `${LIVE_BASE}/${liveId}/join-requests/${requestId}/approve`
      );
      return normalizeResponse(res);
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to approve request"
      );
    }
  }
);

export const rejectJoinRequest = createAsyncThunk(
  "medicalCases/rejectJoinRequest",
  async ({ liveId, requestId }, { rejectWithValue }) => {
    try {
      const res = await axiosGateway.patch(
        `${LIVE_BASE}/${liveId}/join-requests/${requestId}/reject`
      );
      return normalizeResponse(res);
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to reject request"
      );
    }
  }
);

export const leaveLive = createAsyncThunk(
  "medicalCases/leaveLive",
  async (liveId, { rejectWithValue }) => {
    try {
      if (!liveId) throw new Error("liveId is required");
      const res = await axiosGateway.post(`${LIVE_BASE}/${liveId}/leave`);
      return normalizeResponse(res);
    } catch (err) {
      console.error("[medicalCasesThunks] leaveLive error:", err);
      return rejectWithValue(
        err?.response?.data?.message || "Failed to leave live"
      );
    }
  }
);

export const endLive = createAsyncThunk(
  "medicalCases/endLive",
  async (liveId, { rejectWithValue }) => {
    try {
      if (!liveId) throw new Error("liveId is required");
      const res = await axiosGateway.post(`${LIVE_BASE}/${liveId}/end`);
      return normalizeResponse(res);
    } catch (err) {
      console.error("[medicalCasesThunks] endLive error:", err);
      return rejectWithValue(
        err?.response?.data?.message || "Failed to end live"
      );
    }
  }
);

export const cancelLive = createAsyncThunk(
  "medicalCases/cancelLive",
  async (liveId, { rejectWithValue }) => {
    try {
      if (!liveId) throw new Error("liveId is required");
      const res = await axiosGateway.post(`${LIVE_BASE}/${liveId}/cancel`);
      return normalizeResponse(res);
    } catch (err) {
      console.error("[medicalCasesThunks] cancelLive error:", err);
      return rejectWithValue(
        err?.response?.data?.message || "Failed to cancel live"
      );
    }
  }
);

export const createLiveComment = createAsyncThunk(
  "medicalCases/createLiveComment",
  async ({ liveId, content }, { rejectWithValue }) => {
    try {
      if (!liveId) throw new Error("liveId is required");
      const res = await axiosGateway.post(`${LIVE_BASE}/${liveId}/comments`, {
        content,
      });
      return normalizeResponse(res);
    } catch (err) {
      console.error("[medicalCasesThunks] createLiveComment error:", err);
      return rejectWithValue(
        err?.response?.data?.message || "Failed to create live comment"
      );
    }
  }
);

export const fetchLiveComments = createAsyncThunk(
  "medicalCases/fetchLiveComments",
  async (liveId, { rejectWithValue }) => {
    try {
      if (!liveId) throw new Error("liveId is required");
      const res = await axiosGateway.get(`${LIVE_BASE}/${liveId}/comments`);
      return normalizeResponse(res);
    } catch (err) {
      console.error("[medicalCasesThunks] fetchLiveComments error:", err);
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch live comments"
      );
    }
  }
);

export const updateLiveComment = createAsyncThunk(
  "medicalCases/updateLiveComment",
  async ({ liveId, commentId, content }, { rejectWithValue }) => {
    try {
      if (!liveId || !commentId)
        throw new Error("liveId and commentId are required");
      const res = await axiosGateway.patch(
        `${LIVE_BASE}/${liveId}/comments/${commentId}`,
        { content }
      );
      return normalizeResponse(res);
    } catch (err) {
      console.error("[medicalCasesThunks] updateLiveComment error:", err);
      return rejectWithValue(
        err?.response?.data?.message || "Failed to update live comment"
      );
    }
  }
);

export const deleteLiveComment = createAsyncThunk(
  "medicalCases/deleteLiveComment",
  async ({ liveId, commentId }, { rejectWithValue }) => {
    try {
      if (!liveId || !commentId)
        throw new Error("liveId and commentId are required");
      await axiosGateway.delete(`${LIVE_BASE}/${liveId}/comments/${commentId}`);
      return { liveId, commentId };
    } catch (err) {
      console.error("[medicalCasesThunks] deleteLiveComment error:", err);
      return rejectWithValue(
        err?.response?.data?.message || "Failed to delete live comment"
      );
    }
  }
);

export { normalizeList };
